import React, { useState, useEffect, useRef } from 'react';
import { Card, Badge, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { Pup, Reaction } from '../types/breed';
import { reactToPup, getPup } from '../services/pupService';
import { useAuth } from '../context/AuthContext';

interface PupCardProps {
  pup: Pup;
}

const PupCard: React.FC<PupCardProps> = ({ pup }) => {
  const { isAuthenticated, user } = useAuth();
  const [reactions, setReactions] = useState<Record<string, number>>(pup.reactions || {});
  const [userReactions, setUserReactions] = useState<Set<string>>(new Set());
  const [isReacting, setIsReacting] = useState<boolean>(false);

  // Track which reactions the current user has made
  useEffect(() => {
    if (isAuthenticated && user) {
      const fetchUserReactions = async () => {
        try {
          const pupData = await getPup(pup.id);
          setReactions(pupData.reactions || {});

          // Set the user reactions from the backend response
          if (pupData.user_reactions && pupData.user_reactions.length > 0) {
            setUserReactions(new Set(pupData.user_reactions));
          }
        } catch (error) {
          console.error('Failed to fetch user reactions:', error);
        }
      };

      fetchUserReactions();
    }
  }, [pup.id, isAuthenticated, user]);

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const handleReaction = async (reactionType: Reaction['reaction_type']) => {
    if (!isAuthenticated || isReacting) return;

    try {
      setIsReacting(true);
      const response = await reactToPup(pup.id, reactionType);

      if (response.success) {
        // Fetch updated pup data to get current reaction counts
        const updatedPup = await getPup(pup.id);
        setReactions(updatedPup.reactions || {});

        // Update the user reactions locally (toggling)
        setUserReactions(prevReactions => {
          const newReactions = new Set(prevReactions);
          if (response.action === 'added') {
            newReactions.add(reactionType);
          } else if (response.action === 'removed') {
            newReactions.delete(reactionType);
          }
          return newReactions;
        });
      }
    } catch (error) {
      console.error('Failed to react:', error);
    } finally {
      setIsReacting(false);
    }
  };

  return (
    <Card className="pup-card">
      <Link to={`/pups/${pup.id}`} className="card-img-link">
        <div className="card-img-container">
          <Card.Img
            variant="top"
            src={pup.image_url}
            alt={`${pup.breed} dog${pup.dog_name ? ` named ${pup.dog_name}` : ''}`}
            style={{ cursor: 'pointer' }}
          />
        </div>
      </Link>
      <Card.Body>
        {pup.dog_name && (
          <Card.Title>
            <Link to={`/pups/${pup.id}`} style={{ textDecoration: 'none', color: 'inherit' }}>
              {pup.dog_name}
            </Link>
          </Card.Title>
        )}

        <div className="d-flex align-items-center mb-2">
          <Link to={`/breeds/${pup.breed_id}`} className="text-decoration-none">
            <Badge className="breed-badge">{pup.breed}</Badge>
          </Link>
          {pup.is_rare && (
            <Badge className="rare-badge ms-1">Rare</Badge>
          )}
        </div>

        {pup.caption && <Card.Text>{pup.caption}</Card.Text>}

        {pup.location_name && (
          <Card.Text>
            <small className="text-muted">📍 {pup.location_name}</small>
          </Card.Text>
        )}

        <div className="reaction-buttons-container mt-3">
          <OverlayTrigger
            placement="top"
            overlay={<Tooltip id={`tooltip-cute-${pup.id}`}>Cute</Tooltip>}
          >
            <span className="d-inline-block">
              <button
                className={`reaction-button ${userReactions.has('cute') ? 'active' : ''}`}
                onClick={() => handleReaction('cute')}
                disabled={!isAuthenticated || isReacting}
              >
                😍 {reactions.cute || 0}
              </button>
            </span>
          </OverlayTrigger>

          <OverlayTrigger
            placement="top"
            overlay={<Tooltip id={`tooltip-want-it-${pup.id}`}>Good boy</Tooltip>}
          >
            <span className="d-inline-block">
              <button
                className={`reaction-button ${userReactions.has('want_it') ? 'active' : ''}`}
                onClick={() => handleReaction('want_it')}
                disabled={!isAuthenticated || isReacting}
              >
                🦴 {reactions.want_it || 0}
              </button>
            </span>
          </OverlayTrigger>

          <OverlayTrigger
            placement="top"
            overlay={<Tooltip id={`tooltip-fluffy-${pup.id}`}>Fluffy</Tooltip>}
          >
            <span className="d-inline-block">
              <button
                className={`reaction-button ${userReactions.has('fluffy') ? 'active' : ''}`}
                onClick={() => handleReaction('fluffy')}
                disabled={!isAuthenticated || isReacting}
              >
                🧸 {reactions.fluffy || 0}
              </button>
            </span>
          </OverlayTrigger>

          <OverlayTrigger
            placement="top"
            overlay={<Tooltip id={`tooltip-sad-${pup.id}`}>Aww</Tooltip>}
          >
            <span className="d-inline-block">
              <button
                className={`reaction-button ${userReactions.has('sad') ? 'active' : ''}`}
                onClick={() => handleReaction('sad')}
                disabled={!isAuthenticated || isReacting}
              >
                🥺 {reactions.sad || 0}
              </button>
            </span>
          </OverlayTrigger>
        </div>
      </Card.Body>
      <Card.Footer>
        <small className="text-muted card-footer-text">
          {formatDate(pup.created_at)}
          {pup.username && (
            <> by <Link to={`/users/${pup.username}`} className="user-link">{pup.username}</Link></>
          )}
        </small>
      </Card.Footer>
    </Card>
  );
};

export default PupCard;
